<div class="panel-card">
  <div class="tabs-navigation" *ngIf="wound">
    <div
      class="tab-btn"
      [class.active]="activeTab === 'detail'"
      (click)="switchTab('detail')"
    >
      {{ "patientCreate.woundAssessment.woundDetail" | translate }}
    </div>
    <div
      class="tab-btn"
      [class.active]="activeTab === 'history'"
      (click)="switchTab('history')"
    >
      {{ "patientCreate.woundAssessment.woundHistory" | translate }}
    </div>
  </div>

  <!-- Wound Detail Tab Content -->
  <div class="treatment-detail" *ngIf="wound && activeTab === 'detail'">
    <div class="detail-content">
      <section class="detail-section">
        <h3 class="detail-section-title">Overview</h3>

        <div class="overview-grid">
          <article class="overview-card">
            <div class="overview-heading">
              <i class="fa-regular fa-calendar-check"></i>
              <span class="overview-label">{{
                "patientCreate.woundAssessment.onset" | translate
              }}</span>
            </div>
            <div class="overview-body">
              <strong class="overview-value">{{ wound.onset || "-" }}</strong>
            </div>
          </article>

          <article class="overview-card">
            <div class="overview-heading">
              <i class="fa-solid fa-chart-line"></i>
              <span class="overview-label">{{
                "patientCreate.woundAssessment.painLevel" | translate
              }}</span>
            </div>
            <div class="overview-body">
              <strong class="overview-value">
                {{ getPainLevelDisplay(wound.painLevel) }}
              </strong>
            </div>
          </article>

          <article class="overview-card">
            <div class="overview-heading">
              <i class="fa-solid fa-arrow-trend-up"></i>
              <span class="overview-label">{{
                "patientCreate.woundAssessment.healingStatus" | translate
              }}</span>
            </div>
            <div class="overview-body">
              <strong class="overview-value">{{
                getHealingStatusName(wound.healingStatus)
              }}</strong>
            </div>
          </article>

          <article class="overview-card">
            <div class="overview-heading">
              <i class="fa-regular fa-clipboard"></i>
              <span class="overview-label">{{
                "patientCreate.woundAssessment.priorManagement" | translate
              }}</span>
            </div>
            <div class="overview-body">
              <strong class="overview-value overview-value-wrap">
                {{ getPriorManagementDisplay(wound.priorManagement) }}
              </strong>
            </div>
          </article>

          <article class="overview-card">
            <div class="overview-heading overview-heading-plain">
              <span class="overview-label">Wound Type</span>
            </div>
            <div class="overview-body overview-body-plain">
              <div
                class="detail-chip-row"
                *ngIf="wound.woundSymptoms?.length; else noSymptoms"
              >
                <span
                  class="detail-chip detail-chip-orange"
                  *ngFor="let symptom of wound.woundSymptoms"
                >
                  {{ symptom.name }}
                </span>
              </div>
              <ng-template #noSymptoms>
                <strong class="overview-value">-</strong>
              </ng-template>
            </div>
          </article>

          <article class="overview-card">
            <div class="overview-heading overview-heading-plain">
              <span class="overview-label">Primary Diagnosis</span>
            </div>
            <div class="overview-body overview-body-plain">
              <div
                class="detail-chip-row"
                *ngIf="wound.woundUlcerWoundTypes?.length; else noWoundTypes"
              >
                <span
                  class="detail-chip detail-chip-purple"
                  *ngFor="let type of wound.woundUlcerWoundTypes"
                >
                  {{ type.name }}
                </span>
              </div>
              <ng-template #noWoundTypes>
                <strong class="overview-value">-</strong>
              </ng-template>
            </div>
          </article>
        </div>
      </section>

      <section class="detail-section treatment-information">
        <h3 class="detail-section-title">Treatment Information</h3>

        <div
          class="treatment-info-panel"
          *ngIf="hasTreatmentInfo(wound); else noTreatmentData"
        >
          <div
            class="treatment-info-group"
            *ngIf="wound.woundAntibiotics?.length"
          >
            <h4 class="treatment-info-title">
              {{ "patientCreate.woundAssessment.antibiotics" | translate }}
            </h4>
            <div class="treatment-info-grid">
              <div
                class="treatment-info-item"
                *ngFor="let antibiotic of wound.woundAntibiotics"
              >
                {{ antibiotic.name }}
              </div>
            </div>
          </div>

          <div
            class="treatment-info-group"
            *ngIf="wound.woundDebridementDtos?.length"
          >
            <h4 class="treatment-info-title">
              {{ "patientCreate.woundAssessment.debridement" | translate }}
            </h4>
            <div class="treatment-info-grid">
              <div
                class="treatment-info-item"
                *ngFor="let debridement of wound.woundDebridementDtos"
              >
                {{ debridement.name }}
              </div>
            </div>
          </div>

          <div
            class="treatment-info-group"
            *ngIf="wound.woundWoundCares?.length"
          >
            <h4 class="treatment-info-title">
              {{ "patientCreate.woundAssessment.woundCare" | translate }}
            </h4>
            <div class="treatment-info-grid">
              <div
                class="treatment-info-item"
                *ngFor="let woundCare of wound.woundWoundCares"
              >
                {{ woundCare.name }}
              </div>
            </div>
          </div>
        </div>

        <ng-template #noTreatmentData>
          <div class="treatment-info-panel empty-treatment-panel">
            <div class="empty-treatment-icon">
              <i class="fa-regular fa-clipboard"></i>
            </div>
            <p class="empty-treatment-title">
              {{ "patientCreate.woundAssessment.noTreatmentInfo" | translate }}
            </p>
            <p class="empty-treatment-text">
              Treatment details will appear here when added.
            </p>
          </div>
        </ng-template>
      </section>
    </div>
  </div>

  <!-- Wound History Tab Content -->
  <div class="wound-history" *ngIf="activeTab === 'history'">
    <!-- Loading State -->
    <div class="loading-state" *ngIf="isLoadingHistory">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{
          "patientCreate.woundAssessment.loadingHistory" | translate
        }}</span>
      </div>
      <p class="mt-3">
        {{ "patientCreate.woundAssessment.loadingHistory" | translate }}
      </p>
    </div>

    <!-- Timeline Content -->
    <div class="timeline-container" *ngIf="!isLoadingHistory">
      <div
        class="timeline-item"
        *ngFor="let entry of woundHistory; let isLast = last"
      >
        <!-- Timeline Marker -->
        <div class="timeline-marker">
          <div
            class="timeline-dot"
            [class.completed]="
              entry.woundStatus === eWoundStatus.TreatmentCompleted
            "
          ></div>
          <div class="timeline-line" *ngIf="!isLast"></div>
        </div>

        <div class="timeline-content">
          <!-- Date Header -->
          <div class="date-badge">
            <i class="fa-regular fa-calendar"></i>
            {{ entry.createdDate | date: "dd.MM.yyyy HH:mm" }}
            <span *ngIf="entry.completedDate" class="date-separator">
              <i class="fa-solid fa-arrow-right-long"></i>
            </span>
            <span *ngIf="entry.completedDate">
              {{ entry.completedDate | date: "dd.MM.yyyy HH:mm" }}
            </span>
          </div>

          <div class="history-card modern-card">
            <div
              class="status-section"
              *ngIf="entry.woundStatus !== eWoundStatus.TreatmentCompleted"
            >
              <span class="status-pill">{{
                getStatusTranslationKey(entry.woundStatus) | translate
              }}</span>
            </div>

            <!-- Doctor Treatment Instructions -->
            <div
              class="section-block"
              *ngIf="
                entry.woundStatus >= eWoundStatus.TreatmentDecisionCreated &&
                entry.doctorTreatments?.length
              "
            >
              <h4 class="section-label">
                {{
                  "patientCreate.woundAssessment.appliedTreatments" | translate
                }}
              </h4>
              <div class="treatment-notes-list">
                <div
                  class="treatment-note-item"
                  *ngFor="let treatment of entry.doctorTreatments"
                >
                  <div class="treatment-note-header">
                    <span class="treatment-type-badge text-uppercase">
                      {{
                        "conclusionPage." +
                          (getTreatmentType(treatment) | lowercase) | translate
                      }}
                    </span>
                  </div>
                  <p class="treatment-note-text">
                    {{ getTreatmentDescription(treatment) }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Doctor Description -->
            <div
              class="doctor-decision-box"
              *ngIf="
                entry.woundStatus >= eWoundStatus.TreatmentDecisionCreated &&
                entry.doctorDescription
              "
            >
              <div class="box-header">
                <div class="icon-circle doctor-icon">
                  <i class="fa-solid fa-user-doctor"></i>
                </div>
                <span>{{
                  "patientCreate.woundAssessment.doctorDecision" | translate
                }}</span>
              </div>
              <p class="box-text">{{ entry.doctorDescription }}</p>
            </div>

            <!-- Nurse Note -->
            <div
              class="nurse-note-box"
              *ngIf="
                entry.woundStatus === eWoundStatus.TreatmentCompleted &&
                entry.nurseNote
              "
            >
              <div class="box-header">
                <div class="icon-circle nurse-icon">
                  <i class="fa-solid fa-clipboard-check"></i>
                </div>
                <span>{{
                  "patientCreate.woundAssessment.conclusionNote" | translate
                }}</span>
              </div>
              <p class="box-text">{{ entry.nurseNote }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div class="empty-state" *ngIf="!woundHistory?.length && !isLoadingHistory">
      <p>{{ "patientCreate.woundAssessment.noHistoryData" | translate }}</p>
    </div>
  </div>

  <div class="empty-state" *ngIf="!wound?.woundAssessments?.length">
    <p>{{ "patientDetail.noAssessments" | translate }}</p>
  </div>
</div>
